// Horizontal Scroll Animation
class HorizontalScroll {
  constructor() {
    // Implement singleton with refresh capability for Theme Editor
    if (window.__HorizontalScroll__) {
      window.__HorizontalScroll__.refresh();
      return;
    }
    window.__HorizontalScroll__ = this;
    
    this.visibleSections = new Set(); // Track visible sections
    this.init();
    this.bindThemeEditorEvents();
  }

  init() {
    this.horizontalSections = document.querySelectorAll('.horizontal-track');
    if (!this.horizontalSections.length) return;

    this.bindEvents();
    this.checkReducedMotion();
    this.setupIntersectionObserver();
  }

  refresh() {
    this.horizontalSections = document.querySelectorAll('.horizontal-track');
    this.visibleSections.clear();
    if (this.observer) {
      this.observer.disconnect();
    }
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    // Create intersection observer to track when sections enter/exit viewport
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.visibleSections.add(entry.target);
        } else {
          this.visibleSections.delete(entry.target);
        }
      });
    }, {
      threshold: 0.1, // Trigger when 10% of the section is visible
      rootMargin: '50px 0px' // Start animation slightly before section is fully visible
    });

    // Observe all horizontal sections
    this.horizontalSections.forEach(section => {
      this.observer.observe(section);
    });
  }

  bindEvents() {
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true });
  }

  bindThemeEditorEvents() {
    // Handle Shopify Theme Editor section loading/unloading
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.matches('.horizontal-track') ? event.target : event.target.querySelector('.horizontal-track');
      if (section) {
        this.refresh();
      }
    });

    document.addEventListener('shopify:section:unload', () => {
      this.refresh();
    });
  }

  checkReducedMotion() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  handleScroll() {
    // Only animate visible sections
    this.visibleSections.forEach(section => {
      this.updateHorizontalPosition(section);
    });
  }

  handleResize() {
    // Recalculate on resize if needed
    this.handleScroll();
  }

  updateHorizontalPosition(section) {
    const camera = section.querySelector('.horizontal-camera');
    const frame = section.querySelector('.horizontal-frame');
    const items = section.querySelectorAll('.horizontal-item');
    
    if (!camera || !frame || !items.length) return;

    // Skip animation for reduced motion
    if (this.reducedMotion) {
      frame.style.transform = 'none';
      return;
    }

    const rect = section.getBoundingClientRect();
    const sectionHeight = section.offsetHeight;
    const viewportHeight = window.innerHeight;
    
    // Calculate when section enters viewport (top of section reaches bottom of viewport)
    const sectionTop = rect.top + window.scrollY;
    const scrollStart = sectionTop - viewportHeight;
    const scrollEnd = sectionTop + sectionHeight - viewportHeight;
    const currentScroll = window.scrollY;
    
    // Don't start horizontal animation until well into the section
    // Wait until the user has scrolled at least 70% of a viewport into the section
    const bufferZone = viewportHeight * 0.7; // 70% of viewport height buffer
    const animationStart = scrollStart + bufferZone;
    const animationEnd = scrollEnd;
    
    // Calculate progress (0 to 1) only after significant buffer
    let progress = 0;
    if (currentScroll > animationStart && animationEnd > animationStart) {
      progress = (currentScroll - animationStart) / (animationEnd - animationStart);
      progress = Math.max(0, Math.min(1, progress));
    }
    
    // Calculate horizontal translation
    const maxTranslate = (items.length - 1) * 100;
    const translateX = -progress * maxTranslate;
    
    // Apply transform
    frame.style.transform = `translateX(${translateX}vw)`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new HorizontalScroll());
} else {
  new HorizontalScroll();
}